import * as PopoverPrimitive from 'radix-ui/popover';
import type { ReactNode } from 'react';
import { cx } from '../cx';
import { MOTION } from '../motion';
import { isWithinNestedLayer } from '../nestedLayer';
import { MENU_VIEWPORT } from '../SelectMenu/SelectMenu';

/**
 * Non-modal floating panel anchored to its trigger. For anything a phone user
 * must READ or FILL IN, prefer a BottomSheet: a popover near the top of a small
 * screen ends up under the keyboard.
 */
/**
 * TWO WIDTHS, NAMED (`ui-foundation.md` D1 and D11). A caller states which KIND of panel it
 * is opening; the panel owns what that means in pixels. `default` is the menu-sized column
 * every popover in the product has always been. `wide` is for a panel whose content is a
 * ROW rather than a column - the filter editor's conditions read left to right, like a
 * Finder search-criteria row, and four controls do not fit in 20rem.
 */
export type PopoverWidth = 'default' | 'wide';

export interface PopoverProps {
  readonly trigger: ReactNode;
  /** Local portal host, so a preview inherits its own motion tokens. */
  readonly portalContainer?: HTMLElement | undefined;
  readonly children: ReactNode;
  readonly align?: 'start' | 'center' | 'end';
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  readonly width?: PopoverWidth | undefined;
  /**
   * A SUGGESTIONS PANEL IS NOT A DIALOG (wave 4 round 1, C2-4). Radix names its content
   * `role="dialog"`, which is right for a panel a person moves INTO and wrong for the one
   * ARIA pattern this primitive now also has to serve: a search field whose suggestions
   * appear beneath it keeps focus in the field and the panel is the field's listbox, not a
   * window. The caller states the role it needs; everything else about the panel is this
   * component's.
   */
  readonly role?: 'dialog' | 'listbox' | undefined;
  /**
   * Keeps focus where it is when the panel opens. A field that opens its own suggestions
   * has to go on receiving what is typed, and Apple's search field never hands focus to
   * the list (hig: https://developer.apple.com/design/human-interface-guidelines/search-fields).
   */
  readonly keepFocus?: boolean | undefined;
  /** Matches the panel's width to the field it hangs under, the way a suggestions list does. */
  readonly matchTrigger?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
}

const WIDTHS: Record<PopoverWidth, string> = {
  default: 'w-[min(20rem,calc(100vw-2*var(--gutter)))]',
  wide: 'w-[min(44rem,calc(100vw-2*var(--gutter)))]',
};

export function Popover({
  trigger,
  children,
  align = 'start',
  side = 'bottom',
  open,
  onOpenChange,
  width = 'default',
  role,
  keepFocus = false,
  matchTrigger = false,
  ariaLabel,
  portalContainer,
}: PopoverProps) {
  // Uncontrolled unless the caller passes `open`: under
  // `exactOptionalPropertyTypes` an explicit `undefined` would make Radix think
  // the popover is controlled and permanently closed.
  return (
    <PopoverPrimitive.Root
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange !== undefined ? { onOpenChange } : {})}
    >
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal container={portalContainer}>
        <PopoverPrimitive.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          /**
           * A NESTED OVERLAY IS NOT "OUTSIDE" (`ui/nestedLayer.ts`). A confirmation opened
           * from inside this panel portals to the body, so every outside test this layer has
           * says it is outside - and dismissing here would unmount the confirmation before it
           * painted. Only overlays that DECLARED themselves nested are exempted.
           */
          onInteractOutside={(event) => {
            if (isWithinNestedLayer(event.detail.originalEvent.target)) event.preventDefault();
          }}
          {...(role === undefined ? {} : { role })}
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
          {...(keepFocus ? { onOpenAutoFocus: (event: Event) => event.preventDefault() } : {})}
          className={cx(
            /* THE KIT'S POPOVER MATERIAL, NOT THE MENU'S (wave 4 round 2, item 0c). This
               panel used to wear `MENU_SURFACE`, and a menu hangs over a page while a
               popover hangs over whatever the app happens to have there - which for the
               global search is the sidebar's own rows, legible straight through the
               results in the round-1 walk. The HIG puts the two in different sentences:
               a popover takes the REGULAR material, "when background content might create
               legibility issues, or when components have a significant amount of text,
               such as alerts, sidebars, or popovers" (hig:
               https://developer.apple.com/design/human-interface-guidelines/materials),
               and the kit draws it in `Popovers/{Light,Dark}/North-Center`. */
            'popover-surface layer-popover',
            MOTION.pop,
            MENU_VIEWPORT,
            /* EXACTLY THE FIELD'S WIDTH, and that is not a preference: floating-ui measures
               the clipping ancestors of the TRIGGER, and the sidebar keeps its corners with
               `overflow: hidden` - so a panel wider than the pane was shifted back inside it
               and stopped being left-aligned to the field (measured: the field at x 21, the
               panel at x 34.5). A suggestions list the width of its field is also what
               Apple's own search draws. */
            matchTrigger ? 'w-(--radix-popover-trigger-width)' : WIDTHS[width],
            'p-3',
          )}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
